import { ticketType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { format, parseISO } from "date-fns"
import { useAtomValue } from "jotai"
import { ArrowLeft } from "lucide-react"
import { Suspense, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { resolveOperationRule } from "@/api/client/operation-rule"
import { ownerApi, ownerProperties } from "@/api/client/owner"
import { ticketApi, ticketSchemas } from "@/api/client/ticket"
import type { TicketUpdateBody } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import {
  DEFAULT_CONTRACTOR_NOTE,
  OcAssigneeSelector,
  type TicketCategory,
  TicketCategorySelector,
  TicketStatusSelector,
  type TicketSubCategory,
} from "@/components/ticket"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import { DateTimePickerField } from "@/components/ui/date-time-picker"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { RequiredBadge } from "@/components/ui/required-badge"
import { Select } from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { Textarea } from "@/components/ui/textarea"
import { sessionUserAtom } from "@/store/session"

// Radix UI の Select は空文字列を value として使えないため、センチネル値を使用
const CONTRACTOR_STATUS_UNSELECTED = "__unselected__"

// フォームのスキーマを定義
const formSchema = ticketSchemas.ticketUpdateBody
  .extend({
    title: z.string().trim().min(1, "案件タイトルは必須です"),
    // subCategoryはUIのみで使用（APIには送信しない）
    subCategory: z.string().optional(),
    contractorContactInfo: z.string().max(500),
  })
  .superRefine((data, ctx) => {
    if (data.contractorStatus !== null) {
      if (!data.contractorContactInfo.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "業者連絡先を入力してください",
          path: ["contractorContactInfo"],
        })
      }
      if (!data.contractorName?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "業者名を入力してください",
          path: ["contractorName"],
        })
      }
      if (!data.contractorType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "手配先業者を選択してください",
          path: ["contractorType"],
        })
      }
    }
  })

type FormValues = z.infer<typeof formSchema>

// 日付文字列をDateオブジェクトに変換
const parseDateString = (dateString: string | null | undefined): Date | undefined => {
  if (!dateString) return undefined
  try {
    return parseISO(dateString)
  } catch {
    return undefined
  }
}

// DateオブジェクトをYYYY-MM-DD形式の文字列に変換
const formatDateToString = (date: Date | undefined): string | null => {
  if (!date) return null
  return format(date, "yyyy-MM-dd")
}

function TicketEditPageContent() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const sessionUser = useAtomValue(sessionUserAtom)

  // チケット詳細を取得(手配先業者の解決に owner/property が必要なため同梱)
  const { data: ticketData } = ticketApi.useTicketGetSuspense(
    ticketId || "",
    { withOwner: "true", withProperty: "true" },
    {
      query: { staleTime: 0 },
    }
  )
  const ticket = ticketData?.ticket

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      occurredAt: ticket?.occurredAt || "",
      title: ticket?.title || "",
      category: (ticket?.category || "") as FormValues["category"],
      subCategory: ticket?.subCategory || "",
      status: (ticket?.status || "oc_in_progress") as FormValues["status"],
      ocAssigneeId: ticket?.ocAssigneeId || null,
      alertAt: ticket?.alertAt || null,
      alertComment: ticket?.alertComment || "",
      ocComment: ticket?.ocComment || "",
      contractorStatus: (ticket?.contractorStatus || null) as FormValues["contractorStatus"],
      contractorName: ticket?.contractorName || "",
      contractorScheduledDate: ticket?.contractorScheduledDate || null,
      contractorType: (ticket?.contractorType || null) as FormValues["contractorType"],
      contractorContact: ticket?.contractorContact || "",
      contractorContactInfo: ticket?.contractorContactInfo || "",
      note: ticket?.note || DEFAULT_CONTRACTOR_NOTE,
    },
  })

  useEffect(() => {
    if (!sessionUser?.id) return
    form.setValue("ocAssigneeId", sessionUser.id)
  }, [form, sessionUser?.id])

  // 「オーナー様指定業者」選択時、その物件に適用される運用ルール
  // (物件固有→オーナー既定でフォールバック解決)の指定業者から選べるようにする(E-13)。
  const [selectedDesignatedId, setSelectedDesignatedId] = useState("")
  const isOwnerSpecified =
    form.watch("contractorType") === ticketType.CONTRACTOR_TYPE_OWNER_SPECIFIED
  const ticketOwnerId = ticket?.owner?.id
  const ticketPropertyId = ticket?.property?.id
  const { data: ownerData } = ownerApi.useOwnerGet(
    ticketOwnerId ?? "",
    { withOperationRules: ownerProperties.OwnerGetWithOperationRules.true },
    {
      query: {
        enabled: isOwnerSpecified && !!ticketOwnerId && ticketPropertyId != null,
      },
    }
  )
  const designatedContractors =
    resolveOperationRule(ownerData?.owner.operationRules, ticketPropertyId)
      ?.designatedContractors ?? []

  const handleSelectDesignatedContractor = (value: string) => {
    setSelectedDesignatedId(value)
    // 業者はルール内の配列順で識別する
    const contractor = designatedContractors[Number(value)]
    if (contractor) {
      // 選択時点の業者情報を ticket にスナップショット保存する(設計確定事項)。
      form.setValue("contractorName", contractor.name)
      form.setValue("contractorContact", contractor.contactPerson || "")
      form.setValue("contractorContactInfo", contractor.contactInfo)
    }
  }

  const updateMutation = ticketApi.useTicketUpdate({
    mutation: {
      onSuccess: () => {
        toast.success("案件を更新しました")
        navigate(`/tickets/${ticketId}`, { state: { shouldRefetch: true } })
      },
      onError: (error) => {
        toast.error("案件の更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmit = async (values: FormValues) => {
    if (!ticketId) return

    try {
      // subCategoryフィールドを除外してAPIスキーマに準拠したデータを作成
      const { subCategory, ...submitData } = values

      // 使用していないsubCategoryの警告を抑制
      void subCategory

      // 業者対応状況が未選択の場合、業者関連フィールドをクリア
      if (submitData.contractorStatus === null) {
        submitData.contractorName = ""
        submitData.contractorContact = ""
        submitData.contractorContactInfo = ""
        submitData.contractorScheduledDate = null
        submitData.contractorType = null
      }

      // Zodスキーマでバリデーション
      const validatedData = ticketSchemas.ticketUpdateBody.parse(submitData)

      // API呼び出し
      await updateMutation.mutateAsync({
        id: ticketId,
        data: validatedData as TicketUpdateBody,
      })
    } catch (error) {
      console.error(error)
      // エラーハンドリングはmutationのonErrorで処理される
    }
  }

  const handleCancel = () => {
    navigate(`/tickets/${ticketId}`)
  }

  return (
    <ReactErrorBoundary>
      <div>
        {/* ヘッダーナビゲーション */}
        <div className="flex items-center gap-2 p-6">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
          >
            <ArrowLeft size={14} className="text-[#354267]" />
          </button>
          <span className="text-sm font-semibold text-[#3f4652]">案件詳細に戻る</span>
        </div>

        <div className="max-w-[895px] mx-auto px-6 pb-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              {/* 案件管理情報カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">案件管理情報</h2>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {/* 発生日時 */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[#3f4652]">
                      発生日時
                      <RequiredBadge />
                    </p>
                    <FormField
                      control={form.control}
                      name="occurredAt"
                      render={({ field }) => (
                        <DateTimePickerField value={field.value} onChange={field.onChange} />
                      )}
                    />
                  </div>

                  {/* 案件タイトル */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          案件タイトル
                          <RequiredBadge />
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* カテゴリ */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[#3f4652]">
                      カテゴリ
                      <RequiredBadge />
                    </p>
                    <TicketCategorySelector
                      categoryValue={(form.watch("category") || "") as TicketCategory | ""}
                      subCategoryValue={(form.watch("subCategory") || "") as TicketSubCategory | ""}
                      onCategoryChange={(value) => {
                        form.setValue("category", value as FormValues["category"])
                        form.resetField("subCategory")
                      }}
                      onSubCategoryChange={(value) => form.setValue("subCategory", value)}
                      categoryError={form.formState.errors.category?.message}
                    />
                  </div>

                  {/* ステータス */}
                  <div className="grid grid-cols-3 gap-x-8">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            ステータス
                            <RequiredBadge />
                          </FormLabel>
                          <FormControl>
                            <TicketStatusSelector
                              value={field.value}
                              onChange={field.onChange}
                              error={fieldState.error?.message}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* OC担当者 */}
                  <div className="grid grid-cols-3 gap-x-8">
                    <FormField
                      control={form.control}
                      name="ocAssigneeId"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            OC担当者
                            <RequiredBadge />
                          </FormLabel>
                          <FormControl>
                            <OcAssigneeSelector
                              value={field.value || ""}
                              onChange={field.onChange}
                              error={fieldState.error?.message}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* アラートカード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">アラート</h2>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {/* アラート日時 */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[#3f4652]">アラート日時</p>
                    <FormField
                      control={form.control}
                      name="alertAt"
                      render={({ field }) => (
                        <DateTimePickerField
                          value={field.value || undefined}
                          onChange={field.onChange}
                          defaultTime="00:00"
                        />
                      )}
                    />
                  </div>

                  {/* アラートコメント */}
                  <FormField
                    control={form.control}
                    name="alertComment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          アラートコメント
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* 注意事項カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">注意事項</h2>
                </CardHeader>
                <CardContent className="p-5">
                  <FormField
                    control={form.control}
                    name="ocComment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          OCコメント
                        </FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} className="resize-none" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* 業者依頼情報カード */}
              <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
                <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                  <h2 className="text-sm font-semibold text-[#3f4652]">業者依頼情報</h2>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {/* 業者対応状況 */}
                  <div className="grid grid-cols-3 gap-x-8">
                    <FormField
                      control={form.control}
                      name="contractorStatus"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            業者対応状況
                          </FormLabel>
                          <FormControl>
                            <Select
                              value={field.value || CONTRACTOR_STATUS_UNSELECTED}
                              onChange={(value) =>
                                field.onChange(
                                  value === CONTRACTOR_STATUS_UNSELECTED ? null : value
                                )
                              }
                              placeholder="選択してください"
                              options={[
                                { value: CONTRACTOR_STATUS_UNSELECTED, label: "未選択" },
                                ...ticketType.CONTRACTOR_STATUSES.map(({ key, value }) => ({
                                  value: key,
                                  label: value,
                                })),
                              ]}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* 業者名 */}
                  <FormField
                    control={form.control}
                    name="contractorName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          業者名
                          {form.watch("contractorStatus") !== null && <RequiredBadge />}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* 業者対応予定日 */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[#3f4652]">
                      業者対応予定日
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="contractorScheduledDate"
                        render={({ field }) => (
                          <DatePicker
                            value={parseDateString(field.value)}
                            onChange={(date) => field.onChange(formatDateToString(date))}
                            placeholder="日付を選択"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* 手配先業者・業者担当者 */}
                  <div className="grid grid-cols-2 gap-x-8">
                    <FormField
                      control={form.control}
                      name="contractorType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            手配先業者
                            {form.watch("contractorStatus") !== null && <RequiredBadge />}
                          </FormLabel>
                          <FormControl>
                            <Select
                              value={field.value || ""}
                              onChange={(value) => field.onChange(value || null)}
                              placeholder="選択してください"
                              options={ticketType.CONTRACTOR_TYPES.map(({ key, value }) => ({
                                value: key,
                                label: value,
                              }))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="contractorContact"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            業者担当者
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-x-8">
                    <FormField
                      control={form.control}
                      name="contractorContactInfo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-semibold text-[#3f4652]">
                            業者連絡先
                            {form.watch("contractorStatus") !== null && <RequiredBadge />}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* オーナー様指定業者を選択(E-13): 物件に適用される運用ルールの指定業者から選ぶ */}
                  {isOwnerSpecified && (
                    <div className="grid grid-cols-2 gap-x-8">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-[#3f4652]">
                          オーナー様指定業者から選択
                        </p>
                        <Select
                          value={selectedDesignatedId || "none"}
                          onChange={handleSelectDesignatedContractor}
                          placeholder="指定業者を選択"
                          options={[
                            { value: "none", label: "未選択" },
                            ...designatedContractors.map((dc, index) => ({
                              value: String(index),
                              label: dc.name,
                            })),
                          ]}
                        />
                        {designatedContractors.length === 0 && (
                          <p className="text-xs text-slate-400">
                            この物件・オーナーに登録された指定業者はありません
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 備考 */}
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">備考</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} className="resize-none" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* 送信ボタン */}
              <div className="flex justify-center gap-4 pt-6">
                <Button
                  type="submit"
                  variant="default"
                  className="min-w-[100px]"
                  disabled={updateMutation.isPending}
                  loading={updateMutation.isPending}
                >
                  保存する
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                >
                  キャンセル
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </ReactErrorBoundary>
  )
}

// 直接 URL ロード時は ProtectedRoute の同期更新中に suspend するため、
// Suspense 境界が無いと React が throw して白画面になる(RoomEditPage と同じ構成)。
export function TicketEditPage() {
  return (
    <ReactErrorBoundary>
      <Suspense
        fallback={
          <div className="p-10 flex flex-col items-center justify-center">
            <Spinner className="text-slate-600 size-10" />
          </div>
        }
      >
        <TicketEditPageContent />
      </Suspense>
    </ReactErrorBoundary>
  )
}
